import Pagination from "@/components/Pagination";
import { useEffect, useState } from "react";
import api, { fmtDate, apiErr, money, waLink, printHtml, escapeHtml } from "@/lib/api";
import { withAuthCode, apiErrAny } from "@/lib/authCode";
import { useAuthCodePrompt } from "@/components/AuthCodeModal";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import Modal from "@/components/Modal";
import { Search, RotateCcw, ArrowLeft, PackageCheck, Printer, Receipt, MessageCircle, FileText } from "lucide-react";
import { returnTicketHtml } from "@/lib/ticket";

const MOTIFS = [
  "Produit défectueux ou abîmé",
  "Erreur de produit",
  "Produit non utilisé",
  "Autre",
];
const RESOLUTIONS = [
  { v: "refund", l: "Remboursement" },
  { v: "exchange", l: "Échange" },
  { v: "credit", l: "Avoir client" },
];
const resLabel = (v) => RESOLUTIONS.find((r) => r.v === v)?.l || "Remboursement";
// Ventilation d'un retour : espèces rendues / dette annulée / avoir accordé.
// Pour les anciens retours (sans ces champs), approximation par la solution choisie.
const retAmounts = (r) => {
  const hasNew = r.cash_refund !== undefined || r.debt_reduction !== undefined || r.credit_issued !== undefined;
  if (hasNew) return { cash: r.cash_refund || 0, debt: r.debt_reduction || 0, credit: r.credit_issued || 0 };
  const amt = r.refund_amount || 0;
  return { cash: r.resolution === "refund" ? amt : 0, debt: 0, credit: r.resolution === "refund" ? 0 : amt };
};

export default function Returns() {
  const { current, settings, sensitiveModes } = useActivity();
  const refundAllowed = ((sensitiveModes || {}).refund_return || "free") !== "forbidden";
  const cur = current?.currency || settings?.currency || "XOF";
  const org = current?.name || settings?.org_name || "Biashara";
  const [sales, setSales] = useState([]);
  const [prodMap, setProdMap] = useState({});
  const [phoneMap, setPhoneMap] = useState({});
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [qty, setQty] = useState({});            // index ligne -> quantité (unité de base)
  const [motif, setMotif] = useState(MOTIFS[0]);
  const [resolution, setResolution] = useState("refund");
  const [restock, setRestock] = useState(true);
  const [saving, setSaving] = useState(false);
  const authPrompt = useAuthCodePrompt();
  const [page, setPage] = useState(1);
  const [receipt, setReceipt] = useState(null);
  const m = (n) => money(n, cur);

  const load = async () => {
    if (!current || current.global) return;
    const [s, h, p] = await Promise.all([
      api.get(`/sales?activity_id=${current.id}`),
      api.get(`/sale-returns?activity_id=${current.id}`),
      api.get(`/products?activity_id=${current.id}`),
    ]);
    setSales(s.data); setHistory(h.data);
    const map = {}; (p.data || []).forEach((x) => { map[x.id] = x; }); setProdMap(map);
    try {
      const c = await api.get(`/parties?activity_id=${current.id}`);
      const pm = {}; (c.data || []).forEach((x) => { if (x.phone) pm[x.id] = x.phone; }); setPhoneMap(pm);
    } catch (e) { /* clients non accessibles : WhatsApp désactivé */ }
  };
  useEffect(() => { load(); setSel(null); setQty({}); /* eslint-disable-next-line */ }, [current]);

  if (!current || current.global) return <div className="text-[#64748B]">Sélectionnez une activité.</div>;

  const term = q.trim().toLowerCase();
  const results = !term ? [] : sales.filter((s) =>
    (s.number || "").toLowerCase().includes(term) || (s.customer_name || "").toLowerCase().includes(term)
  ).slice(0, 8);

  // Conversion en unité de base pour permettre le retour partiel (même une partie d'un conditionnement).
  const lineInfo = (ln) => {
    const factor = (ln.base_qty && ln.quantity) ? ln.base_qty / ln.quantity : 1;
    const baseUnit = prodMap[ln.product_id]?.base_unit || "unité";
    const divisible = !!prodMap[ln.product_id]?.divisible;
    const soldBase = ln.base_qty != null ? ln.base_qty : ln.quantity;
    const remainingBase = Math.max(soldBase - (ln.returned_qty || 0) * factor, 0);
    const pricePerBase = ((ln.unit_price || 0) - (ln.discount || 0)) / factor;
    const pkgLabel = (ln.packaging && ln.packaging !== "base")
      ? `${ln.quantity} ${ln.packaging} (= ${soldBase} ${baseUnit})`
      : `${ln.quantity} ${baseUnit}`;
    return { factor, baseUnit, divisible, soldBase, remainingBase, pricePerBase, pkgLabel };
  };

  const openSale = (s) => { setSel(s); setQty({}); setMotif(MOTIFS[0]); setResolution(refundAllowed ? "refund" : "credit"); setRestock(true); };

  const setLineQty = (i, val, max, decimal) => {
    if (val === "") { setQty((o) => ({ ...o, [i]: "" })); return; }
    let n = parseFloat(String(val).replace(",", ".")); if (isNaN(n) || n < 0) n = 0;
    if (n > max) n = max;
    if (!decimal) n = Math.round(n);
    setQty((o) => ({ ...o, [i]: n }));
  };

  const estRefund = () => (sel?.lines || []).reduce((acc, ln, i) => {
    const base = parseFloat(String(qty[i] ?? "").toString().replace(",", ".")) || 0;
    return acc + base * lineInfo(ln).pricePerBase;
  }, 0);

  // Ventilation AVANT validation (miroir du backend) : espèces remboursées vs dette annulée vs avoir.
  const estBreakdown = () => {
    const refundTotal = estRefund();
    const balance = Math.max(sel?.balance || 0, 0);
    const fully = (sel?.lines || []).every((ln, i) => {
      const selBase = parseFloat(String(qty[i] ?? "").toString().replace(",", ".")) || 0;
      return selBase >= lineInfo(ln).remainingBase - 1e-9;
    });
    const debt = fully ? balance : Math.min(refundTotal, balance);
    const remainder = Math.max(refundTotal - debt, 0);
    return { refundTotal, cash: resolution === "refund" ? remainder : 0, debt, credit: resolution === "refund" ? 0 : remainder };
  };

  const submit = async () => {
    const agg = {};
    (sel.lines || []).forEach((ln, i) => {
      const base = parseFloat(String(qty[i] ?? "").toString().replace(",", ".")) || 0;
      if (base > 0) { const { factor } = lineInfo(ln); const pkgQty = base / factor; agg[ln.product_id] = (agg[ln.product_id] || 0) + pkgQty; }
    });
    const lines = Object.entries(agg).map(([product_id, quantity]) => ({ product_id, quantity }));
    if (lines.length === 0) return toast.error("Indiquez au moins une quantité à retourner");
    setSaving(true);
    try {
      const { data } = await withAuthCode((code) => api.post(`/sales/${sel.id}/return`, { reason: motif, resolution, restock, lines, ...(code ? { auth_code: code } : {}) }), authPrompt.ask);
      toast.success(`Retour enregistré · ${resLabel(resolution)}`);
      setSel(null); setQty({}); await load();
      setReceipt(data);
    } catch (e) { if (!e.cancelled) toast.error(apiErrAny(e, apiErr)); }
    finally { setSaving(false); }
  };

  // ---- Justificatif de retour ----
  const returnHtml = (r) => {
    const f = (n) => `${Number(n || 0).toLocaleString("fr-FR")} ${cur}`;
    const { cash, debt, credit } = retAmounts(r);
    const rows = (r.lines || []).map((l) => `<tr><td>${escapeHtml(l.product_name || "")}</td><td style="text-align:center">${Number(l.quantity).toLocaleString("fr-FR", { maximumFractionDigits: 3 })}</td><td style="text-align:right">${f(l.amount)}</td></tr>`).join("");
    return `<!doctype html><html><head><meta charset="utf-8"><title>Retour ${escapeHtml(r.sale_number)}</title><style>body{font-family:Arial,sans-serif;color:#0F172A;padding:24px;max-width:640px;margin:auto}h1{color:#16324F;margin:0;font-size:22px}.tag{display:inline-block;margin-top:8px;background:#FEF3C7;color:#92400E;font-weight:bold;padding:4px 12px;border-radius:999px;font-size:13px}table{width:100%;border-collapse:collapse;margin-top:16px}th,td{padding:8px 4px;border-bottom:1px solid #E2E8F0;font-size:14px}th{text-align:left;color:#64748B;font-size:11px;text-transform:uppercase}td.l{color:#64748B}tfoot td{font-weight:bold;border:0}</style></head><body><h1>${escapeHtml(org)}</h1><div class="tag">JUSTIFICATIF DE RETOUR</div><table><tr><td class="l">Facture d'origine</td><td style="text-align:right;font-weight:600">${escapeHtml(r.sale_number)}</td></tr><tr><td class="l">Date du retour</td><td style="text-align:right">${new Date(r.created_at || Date.now()).toLocaleString("fr-FR")}</td></tr><tr><td class="l">Client</td><td style="text-align:right">${escapeHtml(r.customer_name || "Comptant")}</td></tr><tr><td class="l">Motif</td><td style="text-align:right">${escapeHtml(r.reason || "")}</td></tr><tr><td class="l">Solution</td><td style="text-align:right">${escapeHtml(resLabel(r.resolution))}</td></tr><tr><td class="l">Remis en stock</td><td style="text-align:right">${r.restock ? "Oui" : "Non"}</td></tr></table><table><thead><tr><th>Produit retourné</th><th style="text-align:center">Qté</th><th style="text-align:right">Montant</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="2" style="text-align:right;color:#64748B;font-weight:normal">Valeur du retour</td><td style="text-align:right;font-weight:normal">${f(r.refund_amount)}</td></tr><tr><td colspan="2" style="text-align:right">Montant remboursé</td><td style="text-align:right">${f(cash)}</td></tr>${debt > 0 ? `<tr><td colspan="2" style="text-align:right;color:#B45309">Dette annulée</td><td style="text-align:right;color:#B45309">${f(debt)}</td></tr>` : ""}${credit > 0 ? `<tr><td colspan="2" style="text-align:right">Avoir client</td><td style="text-align:right">${f(credit)}</td></tr>` : ""}</tfoot></table><p style="margin-top:20px;color:#475569;font-size:13px">Vendeur initial : ${escapeHtml(r.seller_name || "—")} · Retour traité par : ${escapeHtml(r.actor_name || "—")}</p><p style="margin-top:8px;color:#94A3B8;font-size:12px">Émis le ${new Date().toLocaleString("fr-FR")}</p></body></html>`;
  };
  const returnWaText = (r) => {
    const f = (n) => `${Number(n || 0).toLocaleString("fr-FR")} ${cur}`;
    const { cash, debt, credit } = retAmounts(r);
    const items = (r.lines || []).map((l) => `• ${l.product_name} — ${f(l.amount)}`).join("\n");
    return `*${org}* — Justificatif de retour\nFacture ${r.sale_number}\nClient : ${r.customer_name || "Comptant"}\n${items}\n\nMotif : ${r.reason}\nSolution : ${resLabel(r.resolution)}\nMontant remboursé : ${f(cash)}${debt > 0 ? `\nDette annulée : ${f(debt)}` : ""}${credit > 0 ? `\nAvoir client : ${f(credit)}` : ""}\nMerci de votre confiance.`;
  };
  const receiptPhone = receipt ? (phoneMap[receipt.customer_id] || "") : "";

  return (
    <div className="space-y-5">
      {authPrompt.modal}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Retours clients</h1>
        <p className="text-sm text-[#64748B] mt-1">Retour à partir d'une facture · {current.name}</p>
      </div>

      {!sel ? (
        <>
          <div className="card space-y-3">
            <label className="lbl">Rechercher une facture (numéro ou client)</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input className="inp pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ex : V-2026-00004 ou nom du client" data-testid="return-search-input" autoFocus />
            </div>
            {term && results.length === 0 && <div className="text-sm text-[#94A3B8] py-2">Aucune facture trouvée.</div>}
            <div className="divide-y divide-[#E2E8F0]">
              {results.map((s) => (
                <button key={s.id} onClick={() => openSale(s)} data-testid={`return-sale-${s.id}`}
                  className="w-full text-left py-3 flex items-center justify-between gap-3 hover:bg-[#F8FAFC] rounded-lg px-2 transition-colors">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0F172A]">{s.number} <span className="text-xs text-[#94A3B8] font-normal">· {fmtDate(s.created_at)}</span></div>
                    <div className="text-xs text-[#64748B] truncate">{s.customer_name || "Comptant"} · {(s.lines || []).length} article(s)</div>
                  </div>
                  <div className="text-right shrink-0"><div className="font-bold text-[#16324F]">{m(s.total)}</div></div>
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="font-semibold text-[#16324F] mb-3">Retours effectués</div>
            <div className="md:hidden divide-y divide-[#E2E8F0]" data-testid="returns-history-cards">
              {history.slice((page - 1) * 6, page * 6).map((r) => (
                <div key={r.id} className="py-3 first:pt-0" data-testid={`return-history-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0F172A] truncate">{r.sale_number} · {r.customer_name || "Comptant"}</div>
                      <div className="text-xs text-[#64748B]">{fmtDate(r.created_at)} · {resLabel(r.resolution)}</div>
                    </div>
                    <div className="font-bold text-[#16324F] whitespace-nowrap">{m(r.refund_amount)}</div>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 flex-wrap text-xs text-[#64748B]">
                    <span>Stock : {r.restock ? "Oui" : "Non"} · Par {r.actor_name || "—"}</span>
                    <button onClick={() => setReceipt(r)} className="btn-outline !py-1.5 !px-3 min-h-[36px]" data-testid={`return-receipt-m-${r.id}`}><FileText className="w-3.5 h-3.5" /> Justificatif</button>
                  </div>
                </div>
              ))}
              {history.length === 0 && <div className="text-center py-10 text-[#94A3B8] text-sm">Aucun retour enregistré</div>}
            </div>
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full"><thead><tr>
              <th className="th">Date</th><th className="th">Facture</th><th className="th">Client</th>
              <th className="th">Solution</th><th className="th text-right">Montant</th><th className="th">Stock</th><th className="th">Par</th><th className="th text-right">Justificatif</th>
            </tr></thead><tbody>
              {history.slice((page - 1) * 6, page * 6).map((r) => (
                <tr key={r.id} className="row" data-testid={`return-history-${r.id}`}>
                  <td className="td text-xs">{fmtDate(r.created_at)}</td>
                  <td className="td font-medium">{r.sale_number}</td>
                  <td className="td">{r.customer_name || "Comptant"}</td>
                  <td className="td">{resLabel(r.resolution)}</td>
                  <td className="td text-right font-semibold">{m(r.refund_amount)}</td>
                  <td className="td">{r.restock ? "Oui" : "Non"}</td>
                  <td className="td text-xs text-[#64748B]">{r.actor_name}</td>
                  <td className="td text-right"><button onClick={() => setReceipt(r)} className="btn-outline !py-1.5 !px-3" data-testid={`return-receipt-${r.id}`}><FileText className="w-3.5 h-3.5" /> Voir</button></td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={8} className="td text-center py-10 text-[#94A3B8]">Aucun retour enregistré</td></tr>}
            </tbody></table>
            </div>
            <Pagination page={page} setPage={setPage} total={history.length} perPage={6} />
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <button onClick={() => setSel(null)} className="text-sm font-semibold text-[#2563EB] inline-flex items-center gap-1" data-testid="return-back-btn"><ArrowLeft className="w-4 h-4" /> Retour à la recherche</button>
          <div className="card space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-bold text-[#16324F] text-lg">Facture {sel.number}</div>
                <div className="text-xs text-[#64748B]">{sel.customer_name || "Comptant"} · {fmtDate(sel.created_at)}</div>
              </div>
              <div className="text-right"><div className="text-xs text-[#94A3B8]">Total facture</div><div className="font-bold text-[#16324F]">{m(sel.total)}</div></div>
            </div>

            <div className="space-y-2">
              {(sel.lines || []).map((ln, i) => {
                const info = lineInfo(ln);
                const done = info.remainingBase <= 0;
                return (
                  <div key={i} className={`rounded-xl border p-3 ${done ? "border-[#E2E8F0] bg-[#F8FAFC] opacity-60" : "border-[#E2E8F0]"}`} data-testid={`return-line-${i}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-semibold text-[#0F172A]">{ln.product_name}</div>
                        <div className="text-xs text-[#64748B]">Vendu {info.pkgLabel} · {m(info.pricePerBase)} / {info.baseUnit}{done ? " · déjà retourné" : ""}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#94A3B8]">Retour (max {Number(info.remainingBase).toLocaleString("fr-FR", { maximumFractionDigits: 3 })} {info.baseUnit})</span>
                        <input type="text" inputMode="decimal" disabled={done} className="w-24 text-right inp !py-1.5"
                          value={qty[i] ?? ""} placeholder="0"
                          onChange={(e) => setLineQty(i, e.target.value, info.remainingBase, info.divisible)}
                          data-testid={`return-qty-${i}`} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><label className="lbl">Motif</label>
                <select className="inp" value={motif} onChange={(e) => setMotif(e.target.value)} data-testid="return-motif-select">
                  {MOTIFS.map((x) => <option key={x} value={x}>{x}</option>)}
                </select></div>
              <div><label className="lbl">Solution</label>
                <select className="inp" value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid="return-resolution-select">
                  {RESOLUTIONS.filter((r) => r.v !== "refund" || refundAllowed).map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select></div>
              <div><label className="lbl">Remettre en stock</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setRestock(true)} className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${restock ? "bg-[#DCFCE7] border-[#86EFAC] text-[#166534]" : "border-[#E2E8F0] text-[#64748B]"}`} data-testid="return-restock-yes">Oui</button>
                  <button type="button" onClick={() => setRestock(false)} className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${!restock ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#991B1B]" : "border-[#E2E8F0] text-[#64748B]"}`} data-testid="return-restock-no">Non</button>
                </div></div>
            </div>

            {(() => { const bd = estBreakdown(); return (
              <div className="rounded-xl bg-[#EFF6FF] border border-[#DBEAFE] p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#1D4ED8] inline-flex items-center gap-2"><PackageCheck className="w-4 h-4" />Valeur du retour</span>
                  <span className="text-xl font-extrabold text-[#0F172A]" data-testid="return-estimate">{m(bd.refundTotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#334155]">Montant remboursé</span>
                  <span className="font-bold text-[#0F172A]" data-testid="return-estimate-cash">{m(bd.cash)}</span>
                </div>
                {bd.debt > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#B45309]">Dette annulée</span>
                    <span className="font-bold text-[#B45309]" data-testid="return-estimate-debt">{m(bd.debt)}</span>
                  </div>
                )}
                {bd.credit > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#334155]">Avoir client</span>
                    <span className="font-bold text-[#0F172A]" data-testid="return-estimate-credit">{m(bd.credit)}</span>
                  </div>
                )}
              </div>
            ); })()}

            <button onClick={submit} disabled={saving} className="btn-primary w-full justify-center !py-3" data-testid="submit-return-btn">
              <RotateCcw className="w-4 h-4" /> {saving ? "Enregistrement…" : "Valider le retour"}
            </button>
          </div>
        </div>
      )}

      <Modal open={!!receipt} onClose={() => setReceipt(null)} title="Justificatif de retour" testId="return-receipt-modal"
        footer={<>
          <button className="btn-outline" onClick={() => printHtml(returnHtml(receipt))} data-testid="return-receipt-print-btn"><Printer className="w-4 h-4" /> Imprimer</button>
          <button className="btn-outline" onClick={() => printHtml(returnTicketHtml(receipt, { org: receipt.activity_name || org, activityName: null, phone: settings?.company_phone, address: settings?.company_address, fmt: (n) => `${Number(n || 0).toLocaleString("fr-FR")} ${cur}`, resolution: resLabel(receipt.resolution), ...retAmounts(receipt) }))} data-testid="return-receipt-print-ticket-btn" title="Format ticket de caisse"><Receipt className="w-4 h-4" /> Imprimer le ticket</button>
          <button className="btn-success" onClick={() => window.open(waLink(receiptPhone, returnWaText(receipt)), "_blank", "noopener,noreferrer")} disabled={!receiptPhone} data-testid="return-receipt-wa-btn"><MessageCircle className="w-4 h-4" /> WhatsApp</button>
        </>}>
        {receipt && (
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-[#64748B]">Facture d'origine</span><span className="font-semibold">{receipt.sale_number}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Client</span><span>{receipt.customer_name || "Comptant"}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Motif</span><span>{receipt.reason}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Solution</span><span>{resLabel(receipt.resolution)}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Remis en stock</span><span>{receipt.restock ? "Oui" : "Non"}</span></div>
            <div className="border-t border-[#E2E8F0] pt-2 space-y-1">
              {(receipt.lines || []).map((l, i) => (
                <div key={i} className="flex justify-between"><span>{l.product_name} <span className="text-[#94A3B8]">×{Number(l.quantity).toLocaleString("fr-FR", { maximumFractionDigits: 3 })}</span></span><span className="font-medium">{m(l.amount)}</span></div>
              ))}
            </div>
            <div className="border-t border-[#E2E8F0] pt-2 space-y-1">
              <div className="flex justify-between text-sm"><span className="text-[#64748B]">Valeur du retour</span><span className="font-medium" data-testid="return-receipt-total">{m(receipt.refund_amount)}</span></div>
              <div className="flex justify-between text-base font-bold"><span>Montant remboursé</span><span data-testid="return-receipt-cash">{m(retAmounts(receipt).cash)}</span></div>
              {retAmounts(receipt).debt > 0 && <div className="flex justify-between text-base font-bold text-[#B45309]"><span>Dette annulée</span><span data-testid="return-receipt-debt">{m(retAmounts(receipt).debt)}</span></div>}
              {retAmounts(receipt).credit > 0 && <div className="flex justify-between text-base font-bold"><span>Avoir client</span><span data-testid="return-receipt-credit">{m(retAmounts(receipt).credit)}</span></div>}
              {retAmounts(receipt).credit > 0 && receipt.credit_remaining != null && <div className="flex justify-between text-xs text-[#047857]"><span>Avoir restant (utilisable au POS avec le n° {receipt.sale_number})</span><span data-testid="return-receipt-credit-remaining">{m(receipt.credit_remaining)}</span></div>}
            </div>
            {!receiptPhone && <div className="text-xs text-[#94A3B8]">Aucun numéro client enregistré — partage WhatsApp indisponible.</div>}
          </div>
        )}
      </Modal>
    </div>
  );
}
